import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { reviewTemplateKeys } from "@/lib/query";
import type {
  ReviewTemplateCreateInput,
  ReviewTemplateCriteriaInput,
  ReviewTemplateRelationshipsInput,
  ReviewTemplateScopesInput,
  ReviewTemplateUpdateInput,
} from "@/schemas/review-templates";

export interface ReviewTemplateSummary {
  id: string;
  name: string;
  description: string | null;
  status: "draft" | "active" | "archived";
  lockedAt: string | null;
  supersededByTemplateId: string | null;
  criterionCount: number;
  scopeCount: number;
  relationshipCount: number;
  /** Roles and levels this form covers, named. Null when it covers none yet. */
  scopeSummary: string | null;
  /** The relationships it asks, named and in the admin's own order. */
  relationshipSummary: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * One question on a form.
 *
 * `criterionId` points at the review question catalogue. `competencyId` is the
 * question's OPTIONAL link to a competency — null on a question written for
 * the occasion, and what decides whether reviewers see role-and-level
 * expectations under it.
 */
export interface ReviewTemplateCriterionRow {
  id: string;
  criterionId: string;
  title: string;
  criterionHelpText: string | null;
  categoryId: string | null;
  categoryName: string | null;
  competencyId: string | null;
  sortOrder: number;
  weight: number;
  isRequired: boolean;
  helpText: string | null;
  /** Null = asked of everyone; a key = asked only of that relationship. */
  relationshipKey: string | null;
  visibility: "all_readers" | "people_ops";
}

export interface ReviewTemplateScopeRow {
  id: string;
  roleId: string;
  roleName: string;
  roleLevelId: string | null;
  roleLevelName: string | null;
}

export interface ReviewTemplateRelationshipRow {
  id: string;
  relationshipKey: string;
  relationshipName: string;
  isEnabled: boolean;
  instructions: string | null;
}

export interface ReviewTemplateDetail extends ReviewTemplateSummary {
  criteria: ReviewTemplateCriterionRow[];
  scopes: ReviewTemplateScopeRow[];
  relationships: ReviewTemplateRelationshipRow[];
  /** Roles another ACTIVE form also claims — only one of them can win. */
  clashes: { roleId: string; roleLevelId: string | null; otherTemplateNames: string[] }[];
}

/**
 * The server's own message, or a fallback.
 *
 * Worth the few lines: a locked or in-use form answers 409 with a sentence
 * explaining which, and a generic "Save failed" toast would throw away the only
 * part of the response that tells the admin what to do next.
 */
async function messageFrom(res: Response, fallback: string): Promise<string> {
  const json = await res.json().catch(() => null);
  const error = (json as { error?: unknown } | null)?.error;
  return typeof error === "string" && error.length > 0 ? error : fallback;
}

export interface ReviewRelationshipType {
  key: string;
  name: string;
  description: string | null;
  sortOrder: number;
}

export function useReviewRelationshipTypes() {
  return useQuery({
    queryKey: reviewTemplateKeys.relationshipTypes,
    queryFn: async (): Promise<ReviewRelationshipType[]> => {
      const res = await fetch("/api/review-relationship-types");
      if (!res.ok) throw new Error(await messageFrom(res, "Failed to load reviewer types"));
      const json = await res.json();
      return json.data;
    },
  });
}

export function useReviewTemplates(params: { search?: string } = {}) {
  return useQuery({
    queryKey: reviewTemplateKeys.list(params),
    queryFn: async (): Promise<ReviewTemplateSummary[]> => {
      const query = new URLSearchParams({ limit: "100" });
      if (params.search) query.set("search", params.search);
      const res = await fetch(`/api/review-templates?${query}`);
      if (!res.ok) throw new Error(await messageFrom(res, "Failed to load review forms"));
      const json = await res.json();
      return json.data;
    },
  });
}

export function useReviewTemplate(id: string | null) {
  return useQuery({
    queryKey: reviewTemplateKeys.detail(id ?? ""),
    queryFn: async (): Promise<ReviewTemplateDetail> => {
      const res = await fetch(`/api/review-templates/${id}`);
      if (!res.ok) throw new Error(await messageFrom(res, "Failed to load this review form"));
      const json = await res.json();
      return json.data;
    },
    enabled: Boolean(id),
  });
}

export function useCreateReviewTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReviewTemplateCreateInput): Promise<ReviewTemplateSummary> => {
      const res = await fetch("/api/review-templates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error(await messageFrom(res, "Failed to create the review form"));
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewTemplateKeys.all });
      toast.success("Review form created");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

export function useUpdateReviewTemplate(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReviewTemplateUpdateInput) => {
      const res = await fetch(`/api/review-templates/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error(await messageFrom(res, "Failed to save the review form"));
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewTemplateKeys.all });
      toast.success("Review form saved");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

export function useDeleteReviewTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/review-templates/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await messageFrom(res, "Failed to delete the review form"));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewTemplateKeys.all });
      toast.success("Review form deleted");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/**
 * The three child collections share one mutation shape — each is a PUT of the
 * whole list, so the client never has to reason about which rows changed.
 */
function useSetCollection<TInput>(id: string, path: string, noun: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: TInput) => {
      const res = await fetch(`/api/review-templates/${id}/${path}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error(await messageFrom(res, `Failed to save the ${noun}`));
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewTemplateKeys.all });
      toast.success(`${noun[0]?.toUpperCase()}${noun.slice(1)} saved`);
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

export function useSetReviewTemplateCriteria(id: string) {
  return useSetCollection<ReviewTemplateCriteriaInput>(id, "criteria", "criteria");
}

export function useSetReviewTemplateScopes(id: string) {
  return useSetCollection<ReviewTemplateScopesInput>(id, "scopes", "roles and levels");
}

export function useSetReviewTemplateRelationships(id: string) {
  return useSetCollection<ReviewTemplateRelationshipsInput>(id, "relationships", "reviewers");
}
